import React, { useEffect, useMemo, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import CommonService from "../services/common.service";
import { API_URLS } from "../constants/api-urls";

/** Display labels only — no role names (aligned with backend CANONICAL_STAGE_ORDER). */
export const CM_PRODUCTION_STAGE_OPTIONS = [
  { id: "generate_invoice", label: "Generate Invoice" },
  { id: "accept_invoice", label: "Accept Invoice" },
  { id: "generate_eway_bill", label: "Generate E-Way Bill" },
  { id: "eway_bill_tracking", label: "E-Way Bill Tracking" },
  { id: "generate_shipping_manifest", label: "Generate Shipping Manifest" },
  { id: "accept_manifest", label: "Accept Manifest" },
  { id: "generate_grn", label: "Generate GRN" },
  { id: "accept_grn", label: "Accept GRN" },
];

export function labelForProductionStage(id) {
  const row = CM_PRODUCTION_STAGE_OPTIONS.find((o) => o.id === id);
  return row ? row.label : id;
}

function extractApiErrorMessage(err) {
  return err?.response?.data?.message || err?.message || "Request failed";
}

export default function SellerCmProductionProcessModal({ open, rfqId, onClose, onSuccess }) {
  const [selected, setSelected] = useState(() => new Set(CM_PRODUCTION_STAGE_OPTIONS.map((o) => o.id)));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(new Set(CM_PRODUCTION_STAGE_OPTIONS.map((o) => o.id)));
      setSubmitting(false);
    }
  }, [open, rfqId]);

  const allSelected = useMemo(() => selected.size === CM_PRODUCTION_STAGE_OPTIONS.length, [selected]);

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    setSelected(new Set(CM_PRODUCTION_STAGE_OPTIONS.map((o) => o.id)));
  }

  function clearAll() {
    setSelected(new Set());
  }

  async function handleConfirm() {
    const stages = CM_PRODUCTION_STAGE_OPTIONS.map((o) => o.id).filter((id) => selected.has(id));
    if (stages.length === 0) {
      return;
    }
    try {
      setSubmitting(true);
      await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.SELLER_PRODUCTION_PROCESS_START(rfqId), { stages });
      onSuccess?.();
      onClose?.();
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  if (!open || rfqId == null || Number(rfqId) <= 0) return null;

  return (
    <div className="cm-pp-backdrop" role="presentation" onClick={onClose}>
      <div className="cm-pp-modal" role="dialog" aria-modal="true" aria-labelledby="cm-pp-title" onClick={(e) => e.stopPropagation()}>
        <div className="cm-pp-modal__head">
          <h2 id="cm-pp-title" className="cm-pp-modal__title">
            Production process
          </h2>
          <button type="button" className="cm-pp-modal__close" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <p className="cm-pp-modal__sub">
          Select the steps to include. They will run in the standard order. You can track progress from this list after
          you start.
        </p>
        <div className="cm-pp-modal__toolbar">
          <button type="button" className="btn btn-link btn-sm p-0" onClick={selectAll} disabled={allSelected}>
            Select all
          </button>
          <span className="text-muted px-1">·</span>
          <button type="button" className="btn btn-link btn-sm p-0" onClick={clearAll} disabled={selected.size === 0}>
            Clear
          </button>
        </div>
        <ul className="cm-pp-modal__list">
          {CM_PRODUCTION_STAGE_OPTIONS.map((opt) => (
            <li key={opt.id} className="cm-pp-modal__item">
              <label className="cm-pp-modal__label">
                <input
                  type="checkbox"
                  checked={selected.has(opt.id)}
                  onChange={() => toggle(opt.id)}
                  disabled={submitting}
                />
                <span>{opt.label}</span>
              </label>
            </li>
          ))}
        </ul>
        <div className="cm-pp-modal__actions">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => handleConfirm()}
            disabled={submitting || selected.size === 0}
          >
            {submitting ? "Starting…" : "Start"}
          </button>
        </div>
      </div>
    </div>
  );
}
